/**
 * Customize Selection Popup
 *
 * Handles the modal popup for choosing nations/leagues to avoid,
 * specific teams to avoid, and additional criteria.
 * Syncs selections to hidden form inputs for cart line item properties.
 */

const MAX_NATIONS = 3;

class CustomizeSelectionPopup extends HTMLElement {
  connectedCallback() {
    this.overlay = /** @type {HTMLElement|null} */ (this.querySelector('[ref="overlay"]'));
    this.modal = /** @type {HTMLElement|null} */ (this.querySelector('[ref="modal"]'));
    this.triggerButton = /** @type {HTMLElement|null} */ (this.querySelector('[ref="triggerButton"]'));
    this.hiddenNations = /** @type {HTMLInputElement|null} */ (this.querySelector('[ref="hiddenNations"]'));
    this.hiddenTeams = /** @type {HTMLInputElement|null} */ (this.querySelector('[ref="hiddenTeams"]'));
    this.hiddenCriteria = /** @type {HTMLInputElement|null} */ (this.querySelector('[ref="hiddenCriteria"]'));
    this.teamsTextarea = /** @type {HTMLTextAreaElement|null} */ (this.querySelector('[ref="teamsTextarea"]'));
    this.criteriaTextarea = /** @type {HTMLTextAreaElement|null} */ (this.querySelector('[ref="criteriaTextarea"]'));
    this.nationsGrid = /** @type {HTMLElement|null} */ (this.querySelector('[ref="nationsGrid"]'));
    this.tagsContainer = /** @type {HTMLElement|null} */ (this.querySelector('[ref="tagsContainer"]'));

    /** @type {HTMLInputElement[]} */
    this.checkboxes = this.nationsGrid
      ? /** @type {HTMLInputElement[]} */ (Array.from(this.nationsGrid.querySelectorAll('.customize-selection__nation-checkbox')))
      : [];

    // Bind methods
    this._onKeyDown = this._onKeyDown.bind(this);

    // Set up event listeners
    this._setupListeners();
  }

  _setupListeners() {
    // Trigger button
    if (this.triggerButton) {
      this.triggerButton.addEventListener('click', () => this.openModal());
    }

    // Overlay click
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.closeModal());
    }

    // Close button
    const closeButton = this.querySelector('[ref="closeButton"]');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.closeModal());
    }

    // Cancel button
    const cancelBtn = this.querySelector('.customize-selection__cancel');
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => this.closeModal());
    }

    // Proceed button
    const proceedBtn = this.querySelector('.customize-selection__proceed');
    if (proceedBtn) {
      proceedBtn.addEventListener('click', () => this.handleProceed());
    }

    // Nation checkboxes
    if (this.checkboxes) {
      this.checkboxes.forEach((cb) => {
        cb.addEventListener('change', () => this._enforceMaxNations());
      });
    }
  }

  openModal() {
    if (!this.overlay || !this.modal) return;

    this.overlay.style.display = '';
    this.modal.style.display = '';

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Listen for Escape key
    document.addEventListener('keydown', this._onKeyDown);

    // Focus the first interactive element in the modal
    requestAnimationFrame(() => {
      if (!this.modal) return;
      const closeBtn = /** @type {HTMLElement|null} */ (this.modal.querySelector('.customize-selection__close'));
      if (closeBtn) closeBtn.focus();
    });
  }

  closeModal() {
    if (!this.overlay || !this.modal) return;

    this.overlay.style.display = 'none';
    this.modal.style.display = 'none';

    // Restore body scroll
    document.body.style.overflow = '';

    // Remove key listener
    document.removeEventListener('keydown', this._onKeyDown);

    // Return focus to trigger
    if (this.triggerButton) {
      this.triggerButton.focus();
    }
  }

  handleProceed() {
    // Gather selected nations
    const selectedNations = (this.checkboxes || [])
      .filter((cb) => cb.checked)
      .map((cb) => cb.value);

    const teamsValue = this.teamsTextarea ? this.teamsTextarea.value.trim() : '';
    const criteriaValue = this.criteriaTextarea ? this.criteriaTextarea.value.trim() : '';

    // Sync to hidden inputs
    if (this.hiddenNations) {
      this.hiddenNations.value = selectedNations.join(', ');
    }
    if (this.hiddenTeams) {
      this.hiddenTeams.value = teamsValue;
    }
    if (this.hiddenCriteria) {
      this.hiddenCriteria.value = criteriaValue;
    }

    // Close the modal
    this.closeModal();

    // Update button text to show selections were made
    if (this.triggerButton && (selectedNations.length > 0 || teamsValue || criteriaValue)) {
      const span = this.triggerButton.querySelector('.customize-selection__icon');
      const checkSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
      if (span) span.innerHTML = checkSvg;

      // Update text node
      const savedText = this.getAttribute('data-saved-text') || 'Selection Saved ✓';
      const textNodes = Array.from(this.triggerButton.childNodes).filter(
        (n) => n.nodeType === Node.TEXT_NODE
      );
      const lastTextNode = textNodes[textNodes.length - 1];
      if (lastTextNode) {
        lastTextNode.textContent = ` ${savedText}`;
      }

      this.triggerButton.classList.add('customize-selection__trigger--saved');
    }

    // Render tags below the button
    this._renderTags(selectedNations, teamsValue);
  }

  /**
   * Render selected items as tags below the trigger button.
   * @param {string[]} nations
   * @param {string} teamsText
   */
  _renderTags(nations, teamsText) {
    if (!this.tagsContainer) return;

    // Clear existing tags
    this.tagsContainer.innerHTML = '';

    const xSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;

    // Add a tag for each selected nation
    nations.forEach((nation) => {
      const tag = this._createTag(nation, xSvg, 'nation');
      this.tagsContainer.appendChild(tag);
    });

    // Add tags for teams (split by comma or newline)
    if (teamsText) {
      const teams = teamsText.split(/[,\n]+/).map((t) => t.trim()).filter(Boolean);
      teams.forEach((team) => {
        const tag = this._createTag(team, xSvg, 'team');
        this.tagsContainer.appendChild(tag);
      });
    }

    // Show or hide container
    this.tagsContainer.style.display = this.tagsContainer.children.length > 0 ? '' : 'none';
  }

  /**
   * Create a single tag element.
   * @param {string} label
   * @param {string} xSvg
   * @param {'nation'|'team'} type
   * @returns {HTMLElement}
   */
  _createTag(label, xSvg, type) {
    const tag = document.createElement('div');
    tag.className = 'customize-selection__tag';

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'customize-selection__tag-remove';
    removeBtn.innerHTML = xSvg;
    removeBtn.setAttribute('aria-label', `Remove ${label}`);
    removeBtn.addEventListener('click', () => this._removeTag(label, type, tag));

    const text = document.createElement('span');
    text.textContent = label;

    tag.appendChild(removeBtn);
    tag.appendChild(text);
    return tag;
  }

  /**
   * Remove a tag and update the corresponding hidden input & UI state.
   * @param {string} label
   * @param {'nation'|'team'} type
   * @param {HTMLElement} tagEl
   */
  _removeTag(label, type, tagEl) {
    tagEl.remove();

    if (type === 'nation') {
      // Uncheck the matching checkbox
      const cb = this.checkboxes.find((c) => c.value === label);
      if (cb) {
        cb.checked = false;
        this._enforceMaxNations();
      }
      // Update hidden input
      if (this.hiddenNations) {
        const current = this.hiddenNations.value.split(', ').filter((v) => v && v !== label);
        this.hiddenNations.value = current.join(', ');
      }
    } else if (type === 'team') {
      // Update teams textarea & hidden input
      if (this.teamsTextarea && this.hiddenTeams) {
        const teams = this.teamsTextarea.value
          .split(/[,\n]+/)
          .map((t) => t.trim())
          .filter((t) => t && t !== label);
        this.teamsTextarea.value = teams.join(', ');
        this.hiddenTeams.value = teams.join(', ');
      }
    }

    // Hide container if no tags remain
    if (this.tagsContainer && this.tagsContainer.children.length === 0) {
      this.tagsContainer.style.display = 'none';

      // Reset button text if nothing selected
      this._resetTriggerButton();
    }
  }

  /**
   * Reset the trigger button to its original state when all selections removed.
   */
  _resetTriggerButton() {
    if (!this.triggerButton) return;

    // Check if anything is still selected
    const hasNations = this.hiddenNations && this.hiddenNations.value.trim();
    const hasTeams = this.hiddenTeams && this.hiddenTeams.value.trim();
    const hasCriteria = this.hiddenCriteria && this.hiddenCriteria.value.trim();

    if (!hasNations && !hasTeams && !hasCriteria) {
      // Restore original icon
      const span = this.triggerButton.querySelector('.customize-selection__icon');
      const gearSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>`;
      if (span) span.innerHTML = gearSvg;

      // Restore original text
      const buttonText = this.getAttribute('data-button-text') || 'Customize Your Selection';
      const textNodes = Array.from(this.triggerButton.childNodes).filter(
        (n) => n.nodeType === Node.TEXT_NODE
      );
      const lastTextNode = textNodes[textNodes.length - 1];
      if (lastTextNode) {
        lastTextNode.textContent = ` ${buttonText}`;
      }

      this.triggerButton.classList.remove('customize-selection__trigger--saved');
    }
  }

  _enforceMaxNations() {
    if (!this.checkboxes) return;
    const checkedCount = this.checkboxes.filter((cb) => cb.checked).length;

    this.checkboxes.forEach((cb) => {
      if (!cb.checked) {
        cb.disabled = checkedCount >= MAX_NATIONS;
      }
    });
  }

  _onKeyDown(/** @type {KeyboardEvent} */ e) {
    if (e.key === 'Escape') {
      this.closeModal();
    }
  }
}

if (!customElements.get('customize-selection-popup')) {
  customElements.define('customize-selection-popup', CustomizeSelectionPopup);
}
